/**
 * Fetches Smile.io program data and replaces [points_amount] in quick-add modals.
 * Mirrors the Smile points-on-product-page block (lowest variable earning rate).
 */
(function () {
  const API_HOST = 'platform.smile.io';
  let programConfigCache = null;

  function snakeToCamel(str) {
    return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  }

  function cameliseKeys(obj) {
    if (Array.isArray(obj)) return obj.map(cameliseKeys);
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [snakeToCamel(k), cameliseKeys(v)]));
    }
    return obj;
  }

  function getChannelKey() {
    const el = document.querySelector('.smile-shopify-init[data-channel-key]');
    return el?.dataset?.channelKey || el?.getAttribute('data-channel-key') || null;
  }

  function getSmileHeaders(channelKey) {
    const lang = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
    return {
      Accept: 'application/json',
      'Smile-Channel-Key': channelKey,
      'Smile-Client': 'smile-shopify-app-extensions',
      'Content-Type': 'application/json',
      'X-Smile-Language': lang.split('-')[0],
    };
  }

  async function fetchProgramConfiguration(channelKey) {
    if (programConfigCache) return programConfigCache;

    const headers = getSmileHeaders(channelKey);
    const initUrl = `https://${API_HOST}/shopify_extensions/init/points_on_product_page?channel_key=${encodeURIComponent(channelKey)}`;
    const initRes = await fetch(initUrl, { method: 'GET', headers });
    if (!initRes.ok) throw new Error('Smile init request failed');

    const programData = cameliseKeys(await initRes.json());
    const accountId = programData.account?.id;
    if (!accountId) throw new Error('Smile account id missing');

    const rulesUrl = `https://${API_HOST}/shopify_extensions/activity_rules?account_id=${accountId}`;
    const rulesRes = await fetch(rulesUrl, { method: 'GET', headers });
    if (!rulesRes.ok) throw new Error('Smile activity rules request failed');

    const rulesData = cameliseKeys(await rulesRes.json());
    programConfigCache = {
      programData,
      activityRules: rulesData.activityRules || [],
    };
    return programConfigCache;
  }

  function isOnlineOrderRule(rule) {
    return rule?.type === 'shopify_online_order' && rule?.isEnabled !== false;
  }

  function isVariableRule(rule) {
    return rule?.rewardValueType === 'variable';
  }

  function getRuleMultiplier(rule) {
    if (rule?.multiplier != null && Number(rule.multiplier) > 0) {
      return Number(rule.multiplier);
    }

    const variable = rule?.rewardValue?.variable;
    if (!variable) return null;

    const perAmount = Number(variable.perAmount) || 1;
    const value = Number(variable.value) || 0;
    return value / perAmount;
  }

  function getActiveBonuses(bonuses) {
    const now = Date.now();
    return (bonuses || []).filter((bonus) => {
      const ends = bonus.endsAt || bonus.endsAfter;
      if (!ends) return true;
      return new Date(ends).getTime() > now;
    });
  }

  function getBonusOperand(ruleId, bonuses) {
    return getActiveBonuses(bonuses)
      .filter((b) => String(b.activityRuleId) === String(ruleId))
      .reduce((acc, b) => acc * (Number(b.multiplier) || Number(b.operand) || 1), 1);
  }

  function estimatePointsEarningValue(monetaryValue, activityRules, activityRuleBonuses) {
    if (!monetaryValue || monetaryValue <= 0) return 0;

    const onlineRules = (activityRules || []).filter((r) => isOnlineOrderRule(r) && isVariableRule(r));
    if (!onlineRules.length) return 0;

    let minPoints = null;
    for (const rule of onlineRules) {
      const multiplier = getRuleMultiplier(rule);
      if (multiplier == null) continue;
      const bonusOperand = getBonusOperand(rule.id, activityRuleBonuses);
      const points = Math.floor(monetaryValue * multiplier) * bonusOperand;
      if (minPoints === null || points < minPoints) minPoints = points;
    }
    return minPoints || 0;
  }

  function formatPointsLabel(points, singular, plural) {
    const label = points === 1 ? singular : plural;
    return `${points} ${label}`;
  }

  function decodeLiquidString(str) {
    if (!str) return '';
    try {
      let decoded = str;
      if (decoded.includes('%')) decoded = decodeURIComponent(decoded);
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&');
      return decoded;
    } catch {
      return str;
    }
  }

  function decodeSmileSsrElements(container) {
    container.querySelectorAll('[data-ssr-text]').forEach((el) => {
      const raw = (el.textContent || el.innerHTML || '').trim();
      if (raw.includes('%3C') || raw.includes('[points_amount]') || raw.includes('&lt;')) {
        el.innerHTML = decodeLiquidString(raw);
      }
    });
  }

  function getProductId(container) {
    const input = container.querySelector('input[name="product-id"]');
    return input ? String(input.value) : null;
  }

  function isProductDiscounted(container, variant) {
    if (variant?.compareAtPrice != null && variant?.price != null) {
      return Number(variant.compareAtPrice) > Number(variant.price);
    }
    return container.querySelector('.price--on-sale') != null;
  }

  function convertCurrency(amount) {
    const rate = window.Shopify?.currency?.rate;
    if (rate) return amount / Number(rate);
    return amount;
  }

  function getSelectedVariant(container) {
    const script = container.querySelector('script[type="application/json"][data-selected-variant]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function getVariantPriceDollars(container, variant) {
    const selectedVariant = variant || getSelectedVariant(container);

    if (selectedVariant?.price != null) {
      return convertCurrency(Number(selectedVariant.price) / 100);
    }

    return 0;
  }

  function getSmilePointsElements(container) {
    return container.querySelectorAll('.shopify-app-block [data-ssr-text], .shopify-app-block .slp_13efdb');
  }

  function applyPointsLabel(el, pointsLabel) {
    let template = el.dataset.smilePointsTemplate;
    if (!template) {
      template = el.innerHTML;
      if (!template.includes('[points_amount]')) {
        template = template.replace(/\d+\s+[\w\s-]*/i, '[points_amount]');
      }
      el.dataset.smilePointsTemplate = template;
    }
    el.innerHTML = template.replace(/\[points_amount\]/g, pointsLabel);
  }

  function isGuestCustomer() {
    const init = document.querySelector('.smile-shopify-init');
    const customerId = init?.dataset?.customerId;
    return !customerId;
  }

  async function updateSmilePoints(container, variant) {
    const channelKey = getChannelKey();
    if (!channelKey) return;

    const smileBlockRoot = container.querySelector('.shopify-app-block');
    if (!smileBlockRoot) return;

    decodeSmileSsrElements(container);

    try {
      const { programData, activityRules } = await fetchProgramConfiguration(channelKey);
      const pointsProgram = programData.pointsProgram;
      const blockWrapper = smileBlockRoot.closest('.shopify-block');
      const selectedVariant = variant || getSelectedVariant(container);

      if (!pointsProgram?.isEnabled) {
        blockWrapper?.remove();
        return;
      }

      const canGuestsEarn = programData.account?.candidateParticipation === 'earn';
      if (isGuestCustomer() && !canGuestsEarn) {
        blockWrapper?.remove();
        return;
      }

      const productId = getProductId(container);
      const excluded = (programData.excludedShopifyProductIds || []).map(String);
      if (productId && excluded.includes(productId)) {
        blockWrapper?.remove();
        return;
      }

      const discounted = isProductDiscounted(container, selectedVariant);
      const singular = pointsProgram.pointsLabelSingular || 'Point';
      const plural = pointsProgram.pointsLabelPlural || 'Points';

      let points = 0;
      if (!(programData.excludeDiscountedProducts && discounted)) {
        const monetaryValue = getVariantPriceDollars(container, selectedVariant);
        const bonuses = programData.activeActivityRuleBonuses || [];
        points = estimatePointsEarningValue(monetaryValue, activityRules, bonuses);
      }

      const pointsLabel = formatPointsLabel(points, singular, plural);
      getSmilePointsElements(container).forEach((el) => {
        applyPointsLabel(el, pointsLabel);
      });
    } catch (err) {
      console.warn('Smile quick-add points:', err);
    }
  }

  let variantUnsubscriber = null;

  function initSmilePointsInQuickAdd(container) {
    if (!container) return;

    updateSmilePoints(container);

    if (variantUnsubscriber) variantUnsubscriber();
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const productInfo = container.querySelector('product-info');
    if (!productInfo) return;

    const modalSectionId = productInfo.dataset.originalSection || productInfo.dataset.section;

    variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!container.querySelector('product-info')) return;
      if (event?.data?.sectionId !== modalSectionId) return;
      updateSmilePoints(container, event.data.variant);
    });
  }

  function cleanupSmilePointsQuickAdd() {
    if (variantUnsubscriber) {
      variantUnsubscriber();
      variantUnsubscriber = null;
    }
  }

  window.SmileQuickAddPoints = {
    init: initSmilePointsInQuickAdd,
    cleanup: cleanupSmilePointsQuickAdd,
    update: updateSmilePoints,
  };
})();
